import { Collapse, Grid, IconButton } from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import copy from 'copy-to-clipboard';
import MaterialTable, { Column } from 'material-table';
import moment from 'moment';
import React from 'react';
import ReactJson, { OnCopyProps } from 'react-json-view';

import { IBidRequestLiveLogs } from '~/src/dash/api/live_logs/models';
import { CopyButton } from '~/src/dash/components/button';
import { IStore } from '~/src/dash/reducers/store';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { utils } from '~/src/dash/utils/json';
import { mapBidderType } from '~/src/dash/utils/logs-bid';
import { ct, Text } from '~/src/dash/publicaUI';

interface IRow {
  timestamp: string;
  bidderType: string;
  debugInfo: any;
  endpointRequestBody: string;
  endpointRequestBodyType: string;
  idx: number;
}

export interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  logs: IBidRequestLiveLogs[];
}

interface IState extends IBaseState {
  tableColumns: Array<Column<IRow>>;
  tableData: IRow[];
  activeIndex: number;
}

type Globals = '-moz-initial' | 'inherit' | 'initial' | 'revert' | 'unset';
type WhiteSpaceProperty =
  | Globals
  | '-moz-pre-wrap'
  | 'break-spaces'
  | 'normal'
  | 'nowrap'
  | 'pre'
  | 'pre-line'
  | 'pre-wrap';

function dataToRow(data: IBidRequestLiveLogs[], publisherTimezone: string): IRow[] {
  let i = -10;
  const mappedData = (data || [])
    .map((d: IBidRequestLiveLogs): IRow => {
      i = i + 10;

      let endpointRequestBodyType = 'json';
      try {
        JSON.parse(d.endpointRequest);
      } catch (e) {
        endpointRequestBodyType = '';
      }
      // TODO: may have to change endpoint_request to endpointRequest
      return {
        timestamp:
          moment(d.timestamp).tz(publisherTimezone).format('DD-MM-YYYY[\n]HH:mm:ss') +
          '\n' +
          publisherTimezone,
        bidderType: mapBidderType(d.bidderType),
        debugInfo: d.debugInfo,
        endpointRequestBody: d.endpointRequest,
        endpointRequestBodyType,
        idx: i,
      };
    })
    .sort((a: IRow, b: IRow): number => {
      return (
        moment(b.timestamp, 'DD-MM-YYYY HH:mm:ss').unix() -
        moment(a.timestamp, 'DD-MM-YYYY HH:mm:ss').unix()
      );
    });

  // Sort data by timestamp desc
  return mappedData;
}

export const LogBidRequestTable = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      button: {
        margin: theme.spacing.unit,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      tableColumns: [
        {
          title: ct('Timestamp'),
          field: 'timestamp',
          searchable: false,
          width: '40px',
          cellStyle: { whiteSpace: 'pre-wrap' as WhiteSpaceProperty },
        },
        { title: ct('BidderType'), field: 'bidderType', searchable: false },
      ],
      tableData: dataToRow(this.props.logs, this.props.session.publisher.Timezone),
      activeIndex: -1,
    };

    handleCopy = (copyObj: OnCopyProps) => {
      copy(JSON.stringify(copyObj.src, null, '\t'));
    };

    handleClick = (index: number) => {
      const { activeIndex } = this.state;
      const newIndex = activeIndex === index ? -1 : index;

      this.setState({ activeIndex: newIndex });
    };

    componentDidUpdate(prevProps: Readonly<IProps>): void {
      if (prevProps.logs !== this.props.logs) {
        this.setState({
          tableData: dataToRow(this.props.logs, this.props.session.publisher.Timezone),
        });
      }
    }

    render() {
      return (
        <div>
          <MaterialTable
            detailPanel={(rowData: IRow) => {
              return (
                <Grid container direction='column'>
                  {/* Bid Request */}
                  <Grid item>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item>
                        <IconButton onClick={() => this.handleClick(rowData.idx)}>
                          {this.state.activeIndex === rowData.idx ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <Text tKey='BidRequest' />
                      </Grid>
                      <Grid item>
                        <CopyButton copy={JSON.stringify(rowData.debugInfo)} text={ct('Copy')} />
                      </Grid>
                    </Grid>
                    <Collapse in={this.state.activeIndex === rowData.idx}>
                      {rowData.debugInfo.error !== undefined &&
                      rowData.debugInfo.raw !== undefined ? (
                        rowData.debugInfo.raw
                      ) : (
                        <ReactJson
                          displayDataTypes={false}
                          src={rowData.debugInfo}
                          enableClipboard={this.handleCopy}
                        />
                      )}
                    </Collapse>
                  </Grid>
                  {/* Endpoint Request */}
                  <Grid item>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item>
                        <IconButton onClick={() => this.handleClick(rowData.idx + 3)}>
                          {this.state.activeIndex === rowData.idx + 3 ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <Text tKey='EndpointRequest' />
                      </Grid>
                      <Grid item>
                        <CopyButton copy={rowData.endpointRequestBody} text={ct('Copy')} />
                      </Grid>
                    </Grid>
                    <Collapse in={this.state.activeIndex === rowData.idx + 3}>
                      {
                        // <ReactJson displayDataTypes={false} src={rowData.debugInfo}/>
                        rowData.endpointRequestBodyType === 'json' ? (
                          <ReactJson
                            displayDataTypes={false}
                            src={utils.json.SafeParse(rowData.endpointRequestBody)}
                            enableClipboard={this.handleCopy}
                          />
                        ) : (
                          rowData.endpointRequestBody
                        )
                      }
                    </Collapse>
                  </Grid>
                </Grid>
              );
            }}
            style={{
              paddingLeft: '20px',
              paddingRight: '20px',
            }}
            title=''
            options={{
              search: false,
              pageSize: 15,
              pageSizeOptions: [5, 15, 30],
              toolbar: false,
            }}
            columns={this.state.tableColumns}
            data={this.state.tableData}
          />
        </div>
      );
    }
  },
);
